import React, { useState } from "react";
import { QuoteButton } from "./QuoteButton";
import { Map, ShieldCheck, Target, BookMarked, AlertTriangle, CheckSquare, Loader2 } from "lucide-react";

export function StepBlueprint({ assignment, reload }) {
  const [bp, setBp] = useState(assignment.blueprint);
  const ready = !!assignment.confirmed_requirements;

  const onDone = (data) => { setBp(data); reload(); };

  if (!ready) {
    return <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center text-muted-foreground" data-testid="blueprint-locked">Confirm your brief details in the Check step first.</div>;
  }

  if (!bp) {
    return (
      <div className="flex flex-col items-center rounded-2xl border border-border bg-card p-10 text-center" data-testid="blueprint-generate">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#E8F5EE] text-[#0B5D3B] dark:bg-emerald-950 dark:text-[#25D366]"><Map className="h-6 w-6" /></div>
        <h3 className="mt-4 font-display text-xl font-semibold">Generate your blueprint</h3>
        <p className="mt-1 max-w-md text-muted-foreground">A section-by-section plan: purpose, criteria, concepts and word counts. No written paragraphs — the writing stays yours.</p>
        <div className="mt-6"><QuoteButton assignmentId={assignment.id} action="blueprint" label="Generate blueprint" icon={Map} onDone={onDone} testid="generate-blueprint-btn" /></div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="step-blueprint">
      <div className="flex items-start gap-3 rounded-xl integrity-gradient p-4 text-white">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" />
        <p className="text-sm"><strong>Strict integrity mode.</strong> This blueprint contains guidance, concept links and word counts only. No pre-written text is generated.</p>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <p className="font-display text-lg font-semibold">{bp.summary}</p>
        {bp.requirements && (
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            {bp.requirements.format && <span className="rounded-full bg-accent px-2.5 py-1 font-medium">{bp.requirements.format}</span>}
            {bp.requirements.length && <span className="rounded-full bg-accent px-2.5 py-1 font-medium">{bp.requirements.length}</span>}
            {bp.requirements.referencing_style && <span className="rounded-full bg-accent px-2.5 py-1 font-medium">{bp.requirements.referencing_style}</span>}
          </div>
        )}
      </div>

      <div className="space-y-4">
        {bp.sections?.map((s, i) => (
          <div key={i} className="rounded-2xl border border-border bg-card p-6" data-testid={`blueprint-section-${i}`}>
            <div className="flex items-start justify-between gap-3">
              <h4 className="font-display text-lg font-semibold">{i + 1}. {s.title}</h4>
              {s.word_count && <span className="shrink-0 rounded-full bg-[#0B5D3B]/10 px-3 py-1 font-mono text-xs font-semibold text-[#0B5D3B] dark:text-[#25D366]">{s.word_count}</span>}
            </div>
            <p className="mt-2 flex items-start gap-2 text-sm text-muted-foreground"><Target className="mt-0.5 h-4 w-4 shrink-0 text-[#0B5D3B] dark:text-[#25D366]" />{s.purpose}</p>

            {s.rubric_criteria?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {s.rubric_criteria.map((c, j) => <span key={j} className="rounded-md bg-accent px-2 py-0.5 font-mono text-[11px] font-semibold uppercase tracking-wide text-accent-foreground">{c}</span>)}
              </div>
            )}
            {s.concepts?.length > 0 && (
              <div className="mt-3 text-sm">
                <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase text-muted-foreground"><BookMarked className="h-3.5 w-3.5" />Concepts &amp; readings</div>
                <ul className="list-disc space-y-0.5 pl-5 text-muted-foreground">{s.concepts.map((c, j) => <li key={j}>{typeof c === "string" ? c : c.text}</li>)}</ul>
              </div>
            )}
            {s.common_mistakes?.length > 0 && (
              <div className="mt-3 rounded-xl bg-amber-50 p-3 text-sm dark:bg-amber-950/40">
                <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase text-amber-700 dark:text-amber-300"><AlertTriangle className="h-3.5 w-3.5" />Common mistakes</div>
                <ul className="list-disc space-y-0.5 pl-5 text-amber-900 dark:text-amber-200">{s.common_mistakes.map((m, j) => <li key={j}>{m}</li>)}</ul>
              </div>
            )}
          </div>
        ))}
      </div>

      {bp.checklist?.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="mb-3 flex items-center gap-2 font-display font-semibold"><CheckSquare className="h-4 w-4 text-[#0B5D3B] dark:text-[#25D366]" />Pre-submission checklist</div>
          <ul className="space-y-2">{bp.checklist.map((c, i) => <li key={i} className="flex items-start gap-2 text-sm"><CheckSquare className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />{c}</li>)}</ul>
        </div>
      )}

      <div className="flex justify-end">
        <QuoteButton assignmentId={assignment.id} action="blueprint" label="Regenerate blueprint" icon={Map} onDone={onDone} testid="regenerate-blueprint-btn" />
      </div>
    </div>
  );
}
